import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Download, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB"); } catch { return iso; }
};

const COLUMNS = [
  { key: "wtn_reference",       label: "WTN Reference",          width: "min-w-[130px]" },
  { key: "date",                label: "Date",                    width: "min-w-[90px]"  },
  { key: "site_name",           label: "Site Name",               width: "min-w-[170px]" },
  { key: "address",             label: "Address",                 width: "min-w-[240px]" },
  { key: "picked_up_from",      label: "Picked Up From",          width: "min-w-[200px]" },
  { key: "waste_type",          label: "Waste Type",              width: "min-w-[200px]" },
  { key: "ewc_code",            label: "EWC Code",                width: "min-w-[110px]" },
  { key: "quantity",            label: "Quantity",                width: "min-w-[120px]" },
  { key: "carrier",             label: "Carrier",                 width: "min-w-[160px]" },
  { key: "facility",            label: "Facility",                width: "min-w-[160px]" },
  { key: "wtn_saved",           label: "WTN Saved",               width: "min-w-[100px]", bool: true },
  { key: "signed_both_parties", label: "Signed by Both Parties",  width: "min-w-[150px]", bool: true },
];

const csvEscape = (v) => {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export default function WasteNotesRegister() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/waste-notes/register/list")).data || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const raw = search.trim().toLowerCase();
    if (!raw) return rows;
    // Loose form: strip dashes/spaces so "001" matches "D4PS-WTN-001"
    const loose = raw.replace(/[\s-]/g, "");
    return rows.filter(r =>
      COLUMNS.some(c => {
        const v = String(r[c.key] ?? "").toLowerCase();
        if (v.includes(raw)) return true;
        if (loose && v.replace(/[\s-]/g, "").includes(loose)) return true;
        return false;
      })
    );
  }, [rows, search]);

  const exportCsv = () => {
    const header = COLUMNS.map(c => csvEscape(c.label)).join(",");
    const body = filtered.map(r =>
      COLUMNS.map(c => {
        if (c.bool) return r[c.key] ? "Yes" : "No";
        if (c.key === "date") return fmtDate(r[c.key]);
        return csvEscape(r[c.key]);
      }).map(csvEscape).join(",")
    ).join("\n");
    const csv = `${header}\n${body}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `D4PS-Waste-Transfer-Register-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="wtn-register-page" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Compliance · Register</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Waste Transfer Note Register</h1>
          <p className="text-sm text-slate-600 mt-1">Full register of every waste transfer note — auto-populated from your saved notes. Export to CSV for your accountant or regulator.</p>
        </div>
        <Button onClick={exportCsv} data-testid="wtn-register-export" className="rounded-sm bg-slate-900 hover:bg-slate-800">
          <Download className="w-4 h-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="wtn-register-search"
            placeholder="Search by WTN number, client / site, address, waste, EWC, facility…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-sm border-slate-300"
          />
        </div>
      </Card>

      <Card className="rounded-sm border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600 uppercase text-[10px] tracking-wider">
              <tr>
                {COLUMNS.map(c => (
                  <th key={c.key} className={`text-left px-3 py-2.5 font-semibold whitespace-nowrap ${c.width}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={COLUMNS.length} className="text-center py-10 text-slate-500">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={COLUMNS.length} className="text-center py-12 text-slate-500">No waste notes yet.</td></tr>
              )}
              {!loading && filtered.map(r => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`wtn-register-row-${r.id}`}>
                  <td className="px-3 py-2.5 font-medium whitespace-nowrap">
                    <Link to={`/waste-notes/${r.id}`} className="hover:underline">{r.wtn_reference}</Link>
                  </td>
                  <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-3 py-2.5 text-slate-700">{r.site_name || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-700">{r.address || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-700">{r.picked_up_from || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-700">{r.waste_type || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-500 tabular-nums whitespace-nowrap">{r.ewc_code || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{r.quantity || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-700">{r.carrier || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-700">{r.facility || "—"}</td>
                  <td className="px-3 py-2.5">
                    {r.wtn_saved
                      ? <Badge className="rounded-sm bg-emerald-100 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Yes</Badge>
                      : <Badge className="rounded-sm bg-slate-100 text-slate-600 hover:bg-slate-100 inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> No</Badge>}
                  </td>
                  <td className="px-3 py-2.5">
                    {r.signed_both_parties
                      ? <Badge className="rounded-sm bg-emerald-100 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Yes</Badge>
                      : <Badge className="rounded-sm bg-amber-100 text-amber-700 hover:bg-amber-100 inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> No</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-[11px] text-slate-500">
        Signed by Both Parties auto-flips to <span className="font-medium">Yes</span> when you upload a signed copy in <Link to="/waste-notes/completed" className="underline">Completed notes</Link>.
      </p>
    </div>
  );
}
